import { PageHero } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { BadgePercent, FileText, Handshake, Phone, Mail } from "lucide-react";
import { BRAND } from "@/config";

const STEPS = [
  { icon: FileText, title: "Share your invoice", desc: "Send a recent supplier invoice, quote or product list by phone or email." },
  { icon: BadgePercent, title: "We review pricing", desc: "Our team checks products, quantities, specifications and delivery details." },
  { icon: Handshake, title: "Match or beat", desc: "Eligible items may be matched or beaten, subject to availability and approval." },
];

const phoneHref = `tel:${BRAND.phone.replace(/[^+\d]/g, "")}`;

export default function PriceMatchPage() {
  return (
    <>
      <PageHero
        chapter="Price Match & Price Beat"
        title="Already buying from another supplier? Let us review their price."
        lead="Brampton Cash & Carry may match or beat eligible wholesale pricing when product specifications, quantities, availability and delivery requirements are comparable."
      />

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-start">
          <div className="space-y-4">
            {STEPS.map(({ icon: Icon, title, desc }, index) => (
              <Reveal key={title} delay={index * 0.08}>
                <div className="flex gap-4 rounded-2xl border border-forest/15 bg-white p-5">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-forest text-butter shrink-0"><Icon className="h-5 w-5" /></span>
                  <div>
                    <h3 className="font-display text-lg text-forest">{title}</h3>
                    <p className="text-sm text-forest/70 mt-1">{desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="rounded-3xl border border-forest/15 bg-white p-7 md:p-9 shadow-hard" data-testid="price-match-statement">
            <p className="overline text-forest/55">Price-Match Statement</p>
            <h2 className="font-display text-3xl text-forest tracking-tight mt-3">We review comparable supplier pricing.</h2>
            <div className="text-forest/75 text-sm md:text-base leading-relaxed mt-5 space-y-4">
              <p>
                Share a recent supplier invoice or quotation with our team. We will review the products, pack sizes, quantities, delivery location and availability before confirming whether an item is eligible for a price match or price beat.
              </p>
              <p>
                Price matching is not guaranteed and may exclude clearance items, promotional pricing, membership pricing, out-of-stock products, delivery fees or items with different specifications.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <a href={phoneHref} data-testid="price-match-phone-link" className="inline-flex items-center justify-center gap-2 rounded-full bg-forest text-butter font-semibold px-6 py-3 text-sm hover:bg-forest-deep transition-colors">
                <Phone className="h-4 w-4" /> Call {BRAND.phone}
              </a>
              <a href={`mailto:${BRAND.email}?subject=Price%20Match%20Review`} data-testid="price-match-email-link" className="inline-flex items-center justify-center gap-2 rounded-full border border-forest/30 text-forest font-semibold px-6 py-3 text-sm hover:bg-forest hover:text-butter transition-colors">
                <Mail className="h-4 w-4" /> Email an Invoice
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
